import { saveAs } from "file-saver";
import * as XLSX from "xlsx-js-style";
import { getDoc } from "firebase/firestore";
import {
  getCurrentFCITotalsByAdviser,
  getFCIStatementsPage,
} from "../../FCIStatements/fciStatementsService";
import { loadAMLClientName } from "../../AMLStatements.js/amlDashboard";
import { getIncomePeriods } from "../../utils/incomePeriods";
import { getIODetailsRef } from "../../../Adviser/Tabs/IODetails/ioDetailsService";
import { getQuilterStatement } from "../services/quilterStatementService";
import { getFinplanStatement } from "../services/finplanStatementService";
import { getSuspenseStatement } from "../services/suspenseStatementService";
import { getMonthlyManualPayments } from "../services/manualPaymentService";
import { getIORecipientMappings } from "../services/ioRecipientMappingService";
import { buildFeesAndChargesBreakdown } from "../components/CommissionStatementPDF/utils/feesAndChargesBreakdown";
import {
  commissionRowKey,
  dedupeCommissionRows,
} from "../components/CommissionStatementPDF/utils/commissionBreakdown";
import { calculateAccountBalances } from "./accountBalances";
import {
  adviserIoDetailsDocumentId,
  uniqueIoRecipientIdsByEmail,
} from "./ioRecipientMapping";
import {
  aggregateIoTotalsByRecipientId,
  ioIntroducerTotal,
  mappedIoIntroducerGroups,
  mappedRecipientIdsByAdviser,
} from "./ioRecipientTargets";
import { includedClosingBalanceRows } from "./closingBalanceEligibility";
import {
  getIncomePeriodStartMonthKey,
  monthKeyFromDate,
} from "./incomePeriod";
import {
  normaliseQuilterRows,
  QUILTER_TABLE_HEADERS,
} from "./quilterStatementParser";
import {
  normaliseSuspenseRows,
  SUSPENSE_TABLE_HEADERS,
} from "./suspenseStatementParser";
import { FINPLAN_TEMPLATE_HEADERS } from "./finplanStatementParser";
import { finplanCommissionRows } from "./finplanAdviserLinks";
import {
  consolidateGlobalSummaryRows,
  databaseAdviserName,
  globalChargeSheetRows,
  limitExcelCellText,
} from "./globalSummaryRows";
import { manualPaymentFeeEntries } from "./manualPaymentCharges";
import { paymentsForMonth } from "./paymentDates";
import { buildPaymentSummary } from "./paymentSummary";
import { buildUnknownUsersCommissionRows } from "./unknownUsersCommissionRows";
import { isAmlGuidelineCharge } from "../constants/guidelineCharges";

const NAVY = "081E56";
const PALE_BLUE = "F3F7FB";
const BORDER = "D6E0EA";
const moneyColumns = new Set([
  "Opening Balance",
  "Total Commission",
  "Total Charged",
  "Payments",
  "Advances",
  "Closing Balance",
  "Amount",
  "Amount Charged",
  "Expected Amount",
  "Variance",
  "Payment Amount",
  "Gross Received",
  "Suspense Amount",
  "Suspense Charge",
]);

const numberValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const number = Number(String(value).replace(/[,£%]/g, ""));
  return Number.isFinite(number) ? number : value;
};
const introducerText = (value) => (Array.isArray(value) ? value : [value])
  .flatMap((item) => String(item ?? "").split(","))
  .map((item) => item.trim())
  .filter(Boolean)
  .join(",\n");

const dateValue = (value) => {
  if (!value) return "";
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date;
};

const heading = (value) => ({
  LinkedAdviser: "Mapped Adviser",
  PolicyAdviser: "Selling Adviser",
  PolicyAccountNumber: "Policy Number",
  PaymentCreated: "Payment Created",
  PaymentAmount: "Payment Amount",
  GrossReceived: "Gross Received",
  NormalisedSplit: "Split",
  SuspenseAmount: "Suspense Amount",
  SuspenseCharge: "Suspense Charge",
  ClientName: "Client Name",
  PolicyProvider: "Policy Provider",
  BusinessType: "Business Type",
  CommissionType: "Commission Type",
}[value] || String(value).replace(/([a-z0-9])([A-Z])/g, "$1 $2"));

const sheetFromRows = (headers, rows, { includeHyperlinks = true } = {}) => {
  const matrix = [
    headers,
    ...rows.map((row) => headers.map(
      (header) => limitExcelCellText(row[header] ?? ""),
    )),
  ];
  const sheet = XLSX.utils.aoa_to_sheet(matrix);
  const range = XLSX.utils.decode_range(sheet["!ref"] || "A1");
  sheet["!autofilter"] = { ref: XLSX.utils.encode_range({ s: range.s, e: { r: 0, c: range.e.c } }) };
  sheet["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2", activePane: "bottomLeft", state: "frozen" };
  sheet["!cols"] = headers.map((header, column) => {
    const longest = rows.reduce(
      (width, row) => Math.max(width, String(row[header] ?? "").length),
      String(header).length,
    );
    return { wch: Math.min(42, Math.max(12, longest + 2)), hidden: column < 0 };
  });
  for (let row = 0; row <= range.e.r; row += 1) {
    for (let column = 0; column <= range.e.c; column += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: column })];
      if (!cell) continue;
      const header = headers[column];
      cell.s = row === 0
        ? {
            fill: { fgColor: { rgb: NAVY } },
            font: { color: { rgb: "FFFFFF" }, bold: true },
            alignment: { vertical: "center", horizontal: moneyColumns.has(header) ? "right" : "left" },
          }
        : {
            fill: { fgColor: { rgb: row % 2 ? "FFFFFF" : PALE_BLUE } },
            border: { bottom: { style: "thin", color: { rgb: BORDER } } },
            alignment: { vertical: "top", horizontal: moneyColumns.has(header) || header === "Split" ? "right" : "left" },
          };
      if (row > 0 && moneyColumns.has(header) && typeof cell.v === "number") cell.z = "£#,##0.00;[Red]-£#,##0.00";
      if (row > 0 && header === "Split" && typeof cell.v === "number") cell.z = "0.####%";
      if (row > 0 && cell.v instanceof Date) cell.z = "dd mmm yyyy hh:mm:ss";
      if (row > 0 && header === "Introducers") {
        cell.s = {
          ...cell.s,
          alignment: { ...(cell.s?.alignment || {}), vertical: "top", wrapText: true },
        };
      }
      const hyperlink = row > 0 && includeHyperlinks
        ? header === "Client Name"
          ? rows[row - 1]?.["Client URL"]
          : header === "Policy Number"
            ? rows[row - 1]?.["Plan URL"]
            : ""
        : "";
      if (hyperlink) {
        cell.l = {
          Target: String(hyperlink),
          Tooltip: header === "Client Name"
            ? "Open client in Intelliflo"
            : "Open plan in Intelliflo",
        };
        cell.s = {
          ...cell.s,
          font: { color: { rgb: "0563C1" }, underline: true },
        };
      }
    }
  }
  return sheet;
};

const balanceSheetFromRows = (headers, rows, amountHeader) => {
  const total = rows.reduce(
    (sum, row) => sum + Number(row[amountHeader] || 0),
    0,
  );
  const rowsWithTotal = [
    ...rows,
    {
      Adviser: "Total",
      [amountHeader]: total,
    },
  ];
  const sheet = sheetFromRows(headers, rowsWithTotal);
  const totalRow = rowsWithTotal.length;
  headers.forEach((header, column) => {
    const cell = sheet[XLSX.utils.encode_cell({ r: totalRow, c: column })];
    if (!cell) return;
    cell.s = {
      ...(cell.s || {}),
      fill: { fgColor: { rgb: "D6E9FD" } },
      font: { ...(cell.s?.font || {}), bold: true, color: { rgb: NAVY } },
      border: {
        ...(cell.s?.border || {}),
        top: { style: "medium", color: { rgb: NAVY } },
      },
    };
  });
  return sheet;
};

const chargeRows = (records) => records.map((record) => {
  const category = isAmlGuidelineCharge(record)
    ? "AML Checks"
    : record.chargeType || "Other Charges";
  return {
    Adviser: record.adviser || "",
    "Adviser Email": record.adviserEmail || "",
    Date: dateValue(record.date),
    "Charge Type": record.manualChargeType || record.chargeType || "",
    __category: category,
    "Charge Description": record.description || "",
    Client: record.client || "",
    Paraplanner: record.paraplanner || "",
    "Amount Charged": numberValue(record.amountCharged),
    "Expected Amount": numberValue(record.expectedAmount),
    Variance:
      Number(record.amountCharged || 0) - Number(record.expectedAmount || 0),
  };
});

const safeSheetName = (value, usedNames) => {
  const base = String(value || "Other Charges")
    .replace(/[\\/?*[\]:]/g, " ")
    .trim()
    .slice(0, 31) || "Other Charges";
  let name = base;
  let suffix = 2;
  while (usedNames.has(name)) {
    const marker = ` ${suffix}`;
    name = `${base.slice(0, 31 - marker.length)}${marker}`;
    suffix += 1;
  }
  usedNames.add(name);
  return name;
};

const appendChargeSheets = (workbook, rows, advisers) => {
  const groups = new Map();
  rows.forEach((row) => {
    const category = row.__category || "Other Charges";
    if (!groups.has(category)) groups.set(category, []);
    groups.get(category).push(row);
  });
  const usedNames = new Set(workbook.SheetNames);
  [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right, "en-GB"))
    .forEach(([category, categoryRows]) => {
      const outputRows = globalChargeSheetRows({
        category,
        rows: categoryRows,
        advisers,
      });
      const headers = [
        "Adviser",
        "Adviser Email",
        "Date",
        "Charge Type",
        "Charge Description",
      ];
      if (outputRows.some((row) => row.Client)) headers.push("Client");
      if (outputRows.some((row) => row.Paraplanner)) headers.push("Paraplanner");
      headers.push("Amount Charged", "Expected Amount", "Variance");
      XLSX.utils.book_append_sheet(
        workbook,
        sheetFromRows(headers, outputRows),
        safeSheetName(category, usedNames),
      );
    });
  if (!rows.length) {
    XLSX.utils.book_append_sheet(
      workbook,
      sheetFromRows(["Adviser", "Adviser Email", "Date", "Charge Type", "Charge Description", "Amount Charged", "Expected Amount", "Variance"], []),
      "Charges",
    );
  }
};

const ioRows = (rows) => rows.map((row) => ({
  Recipient: [row.Recipient, row.RecipientType].filter(Boolean).join(" - "),
  "Payment Created": dateValue(row.PaymentCreated),
  "Selling Adviser": row.PolicyAdviser || "",
  "Client Name": row.ClientName || "",
  "Client URL": row.ClientUrl || "",
  "Policy Number": row.PolicyAccountNumber || "",
  "Plan URL": row.PlanUrl || "",
  "Policy Provider": row.PolicyProvider || "",
  "Business Type": row.BusinessType || "",
  "Commission Type": row.CommissionType || "",
  "Payment Amount": numberValue(row.PaymentAmount),
  "Gross Received": numberValue(row.GrossReceived),
  Split: numberValue(row.NormalisedSplit ?? row.Split) === ""
    ? ""
    : Number(numberValue(row.NormalisedSplit ?? row.Split)) / 100,
  Introducers: introducerText(row.IntroducerName ?? row.Introducers),
}));

const keyOf = (value) => String(value || "").trim().toLowerCase();
const isNonZero = (value) => Math.round(Number(value || 0) * 100) !== 0;

const paymentRows = (payments, selectedMonth) => paymentsForMonth(
  payments,
  selectedMonth,
)
  .map((payment) => ({
    Adviser:
      payment.adviserName ||
      payment.mappedEntityName ||
      payment.sourceName ||
      payment.adviserEmail ||
      "Unknown",
    "Adviser Email": payment.adviserEmail || "",
    Date: dateValue(payment.date),
    Description: payment.description || "",
    Schedule:
      payment.paymentType === "opening_balance"
        ? "One-Time"
        : payment.billingType === "recurring"
          ? "Recurring"
          : "One-Time",
    "Payment To": payment.paymentTo ? dateValue(payment.paymentTo) : "",
    Notes: payment.notes || "",
    Amount: numberValue(
      payment.paymentType === "opening_balance"
        ? payment.openingBalanceAmount
        : payment.paymentType === "additional_payment"
          ? Math.abs(Number(payment.amountPaid || 0))
          : payment.amountPaid,
    ),
    "Payment Type":
      payment.paymentType === "advance"
        ? "Advance"
        : payment.paymentType === "opening_balance"
          ? "Opening Balance"
          : "Additional Payment",
  }));

const uploadedRows = (statement, normalise, headers, advisers = []) => {
  const links = new Map((statement?.adviserLinks || []).map((link) => [String(link.sourceAdviserName || "").trim().toLowerCase(), link]));
  const advisersByEmail = new Map(
    advisers.map((adviser) => [keyOf(adviser.email || adviser.id), adviser]),
  );
  const exportHeaders = headers.filter((key) => key !== "Index");
  return normalise(statement?.rows || []).map((row) => {
    const linked = links.get(String(row.SourceAdviserName || "").trim().toLowerCase());
    const linkedAdviser = advisersByEmail.get(keyOf(linked?.email));
    const linkedName = linkedAdviser
      ? databaseAdviserName(linkedAdviser, linked?.adviserName)
      : linked?.adviserName;
    return exportHeaders.reduce((result, key) => {
      const label = heading(key);
      if (key === "LinkedAdviser") result[label] = linked?.email
        ? `${linkedName || row.MappedAdviserName || ""} (${linked.email})`
        : row.LinkedAdviser || row.MappedAdviserName || "";
      else if (["PaymentAmount", "GrossReceived", "SuspenseAmount", "SuspenseCharge"].includes(key)) result[label] = numberValue(row[key]);
      else result[label] = row[key] || "";
      return result;
    }, {
      __email: keyOf(linked?.email),
      __entryKey: row.SourceAdviserName || row.MappedAdviserName || "",
    });
  });
};

const buildBalanceRows = async ({
  selectedMonth,
  chargeRecords,
  balanceChargeRecords,
  manualPayments,
  ioRows: rawIoRows,
  ioTotals,
  quilterStatement,
  finplanStatement,
  suspenseStatement,
  advisers,
  ioRecipientMappings,
}) => {
  const listedByEmail = new Map(
    advisers.map((adviser) => [keyOf(adviser.email || adviser.id), adviser]),
  );
  const advisersByName = new Map(
    advisers.map((adviser) => [keyOf(adviser.name), adviser]),
  );
  const adviserDetailResults = await Promise.allSettled(
    advisers.map(async (adviser) => {
      const email = keyOf(adviser.email || adviser.id);
      if (!email) return null;
      const snapshot = await getDoc(
        getIODetailsRef(adviserIoDetailsDocumentId(adviser)),
      );
      return {
        email,
        isAppointed: adviser.isAppointed,
        adviser,
        ioDetails: snapshot.exists() ? snapshot.data() : {},
      };
    }),
  );
  const adviserDetails = adviserDetailResults
    .filter((result) => result.status === "fulfilled" && result.value)
    .map((result) => result.value);
  const ioTotalsById = Array.isArray(ioTotals)
    ? aggregateIoTotalsByRecipientId(ioTotals)
    : new Map();
  if (!Array.isArray(ioTotals)) {
    rawIoRows.forEach((row) => {
      const recipientId = keyOf(
        row.RecipientCrmId ??
          row.RecipientUserId ??
          row.RecipientId ??
          row.AdviserId ??
          "",
      );
      if (!recipientId) return;
      const amount = Number(String(row.PaymentAmount ?? 0).replace(/[,£]/g, ""));
      if (Number.isFinite(amount)) {
        ioTotalsById.set(
          recipientId,
          (ioTotalsById.get(recipientId) || 0) + amount,
        );
      }
    });
  }
  const ioTotalsByEmail = new Map();
  const validAdviserDetails = adviserDetails.filter(Boolean);
  const uniqueIds = uniqueIoRecipientIdsByEmail(validAdviserDetails);
  const mappedIds = mappedRecipientIdsByAdviser(ioRecipientMappings);
  validAdviserDetails.forEach(({ email }) => {
    const recipientIds = new Set([
      ...(uniqueIds[email] || []),
      ...(mappedIds.get(email) || []),
    ].map(String));
    ioTotalsByEmail.set(
      email,
      [...recipientIds].reduce(
        (sum, id) => sum + Number(ioTotalsById.get(keyOf(id)) || 0),
        0,
      ),
    );
  });

  const quilterTotalsByEmail = new Map();
  const quilterLinks = new Map(
    (quilterStatement?.adviserLinks || []).map((link) => [
      keyOf(link.sourceAdviserName),
      link,
    ]),
  );
  normaliseQuilterRows(quilterStatement?.rows || []).forEach((row) => {
    const override = quilterLinks.get(keyOf(row.SourceAdviserName));
    const linked = override?.email
      ? override
      : advisersByName.get(keyOf(row.MappedAdviserName));
    const email = keyOf(linked?.email);
    const amount = Number(String(row.PaymentAmount ?? 0).replace(/[,£]/g, ""));
    if (email && Number.isFinite(amount)) {
      quilterTotalsByEmail.set(
        email,
        (quilterTotalsByEmail.get(email) || 0) + amount,
      );
    }
  });

  const finplanTotalsByEmail = new Map();
  finplanCommissionRows({
    statement: finplanStatement,
    advisers,
    statementMonth: selectedMonth,
  }).forEach((row) => {
    const email = keyOf(row.RecipientEmail);
    const amount = Number(String(row.PaymentAmount ?? 0).replace(/[,£]/g, ""));
    if (!email || !Number.isFinite(amount)) return;
    finplanTotalsByEmail.set(
      email,
      (finplanTotalsByEmail.get(email) || 0) + amount,
    );
  });

  const suspenseByEmail = new Map();
  const suspenseLinks = new Map(
    (suspenseStatement?.adviserLinks || []).map((link) => [
      keyOf(link.sourceAdviserName),
      link,
    ]),
  );
  normaliseSuspenseRows(suspenseStatement?.rows || []).forEach((row) => {
    const override = suspenseLinks.get(keyOf(row.SourceAdviserName));
    const linked = override?.email
      ? override
      : advisersByName.get(keyOf(row.MappedAdviserName));
    const email = keyOf(linked?.email);
    if (!email) return;
    suspenseByEmail.set(email, [...(suspenseByEmail.get(email) || []), row]);
  });

  const { balances } = calculateAccountBalances({
    records: balanceChargeRecords,
    payments: manualPayments,
    month: selectedMonth,
  });
  const selectedPayments = paymentsForMonth(manualPayments, selectedMonth);
  const bulkUploadedBalances = new Map();
  const nonAdviserBulkBalances = new Map();
  const nonAdviserOpeningBalances = new Map();
  const openingPayments = manualPayments.filter(
    (payment) =>
      payment.paymentMonth === selectedMonth &&
      payment.paymentType === "opening_balance",
  );
  const nonAdviserIdentity = (payment) => {
    const entityType = String(payment.mappedEntityType || "unknown").toLowerCase();
    const entityId = String(
      payment.mappedEntityId ||
        payment.ioIntroducerId ||
        payment.sourceName ||
        payment.id ||
        "",
    ).trim();
    if (!entityId) return null;
    return {
      entityType,
      rowKey: `${entityType}:${entityId.toLowerCase()}`,
      name: payment.mappedEntityName || payment.sourceName || "Unknown",
    };
  };
  const manuallyUploadedNonAdvisers = new Set(
    openingPayments
      .filter(
        (payment) =>
          !keyOf(payment.adviserEmail) &&
          payment.source !== "closing_balance_carry_forward",
      )
      .map((payment) => nonAdviserIdentity(payment)?.rowKey)
      .filter(Boolean),
  );
  openingPayments.forEach((payment) => {
    const email = keyOf(payment.adviserEmail);
    if (email) {
      if (payment.source === "opening_balance_bulk_upload") {
        bulkUploadedBalances.set(
          email,
          (bulkUploadedBalances.get(email) || 0) +
            Number(payment.openingBalanceAmount || 0),
        );
      }
      return;
    }
    const identity = nonAdviserIdentity(payment);
    if (!identity) return;
    if (
      payment.source === "closing_balance_carry_forward" &&
      manuallyUploadedNonAdvisers.has(identity.rowKey)
    ) return;
    const opening = nonAdviserOpeningBalances.get(identity.rowKey) || {
      ...identity,
      amount: 0,
    };
    opening.amount += Number(payment.openingBalanceAmount || 0);
    nonAdviserOpeningBalances.set(identity.rowKey, opening);
    if (payment.source === "opening_balance_bulk_upload") {
      const bulk = nonAdviserBulkBalances.get(identity.rowKey) || {
        ...identity,
        amount: 0,
      };
      bulk.amount += Number(payment.openingBalanceAmount || 0);
      nonAdviserBulkBalances.set(identity.rowKey, bulk);
    }
  });

  const emails = new Set([
    ...balances.keys(),
    ...chargeRecords.map((record) => keyOf(record.adviserEmail)),
    ...selectedPayments
      .map((payment) => keyOf(payment.adviserEmail)),
    ...bulkUploadedBalances.keys(),
    ...ioTotalsByEmail.keys(),
    ...quilterTotalsByEmail.keys(),
    ...finplanTotalsByEmail.keys(),
    ...suspenseByEmail.keys(),
  ].filter(Boolean));
  const adviserRows = [...emails].map((email) => {
    const adviser = listedByEmail.get(email);
    const openingBalance = Number(balances.get(email) || 0);
    const totalCommission =
      Number(ioTotalsByEmail.get(email) || 0) +
      Number(quilterTotalsByEmail.get(email) || 0) +
      Number(finplanTotalsByEmail.get(email) || 0);
    const manualPaymentCharges = manualPaymentFeeEntries(
      selectedPayments.filter(
        (payment) => keyOf(payment.adviserEmail) === email,
      ),
    );
    const feesAndCharges = buildFeesAndChargesBreakdown(
      [
        ...chargeRecords.filter(
          (charge) => keyOf(charge.adviserEmail) === email,
        ),
        ...manualPaymentCharges,
      ],
      suspenseByEmail.get(email) || [],
    );
    const paymentSummary = buildPaymentSummary({
      openingBalance,
      totalCommission,
      feesAndCharges,
    });
    return {
      email,
      adviser: adviser?.name ||
        openingPayments.find((payment) => keyOf(payment.adviserEmail) === email)?.adviserName ||
        chargeRecords.find((charge) => keyOf(charge.adviserEmail) === email)?.adviser ||
        email,
      practice: adviser?.brandName ||
        openingPayments.find((payment) => keyOf(payment.adviserEmail) === email)?.brandName ||
        "-",
      openingBalance,
      hasOpeningBalance: balances.has(email),
      bulkUploadedOpeningBalance: bulkUploadedBalances.get(email) || 0,
      hasBulkUploadedOpeningBalance: bulkUploadedBalances.has(email),
      totalCommission,
      totalCharged: paymentSummary.totalCharged,
      totalPayments: paymentSummary.payments,
      totalAdvances: paymentSummary.advances,
      closingBalance: paymentSummary.balanceToPay,
      isUnknown: false,
    };
  });
  const nonAdviserKeys = new Set([
    ...nonAdviserOpeningBalances.keys(),
    ...nonAdviserBulkBalances.keys(),
  ]);
  const introducerGroupsByRowKey = new Map(
    mappedIoIntroducerGroups([], ioRecipientMappings).map((group) => {
      const rowKey = `introducer:${keyOf(group.targetId)}`;
      nonAdviserKeys.add(rowKey);
      return [rowKey, group];
    }),
  );
  const nonAdviserRows = [...nonAdviserKeys].map((rowKey) => {
    const entity =
      nonAdviserOpeningBalances.get(rowKey) ||
      nonAdviserBulkBalances.get(rowKey) || {
        entityType: "introducer",
        rowKey,
        name: introducerGroupsByRowKey.get(rowKey)?.name || "IO Introducer",
        amount: 0,
      };
    const bulkEntity = nonAdviserBulkBalances.get(rowKey);
    const mappedEntityId = rowKey.slice(rowKey.indexOf(":") + 1);
    const introducerGroup = introducerGroupsByRowKey.get(rowKey);
    const totalCommission = entity.entityType === "introducer"
      ? ioIntroducerTotal({
          totalsByRecipientId: ioTotalsById,
          targetId: mappedEntityId,
          recipientIds: introducerGroup?.recipientIds,
        })
      : 0;
    const feesAndCharges = buildFeesAndChargesBreakdown(
      manualPaymentFeeEntries(
        selectedPayments.filter(
          (payment) =>
            !keyOf(payment.adviserEmail) &&
            nonAdviserIdentity(payment)?.rowKey === rowKey,
        ),
      ),
    );
    const paymentSummary = buildPaymentSummary({
      openingBalance: entity.amount,
      totalCommission,
      feesAndCharges,
    });
    return {
      email: "",
      rowKey,
      adviser: entity.name,
      practice: entity.entityType === "introducer" ? "IO Introducer" : "Unknown",
      openingBalance: entity.amount,
      hasOpeningBalance: nonAdviserOpeningBalances.has(rowKey),
      bulkUploadedOpeningBalance: bulkEntity?.amount || 0,
      hasBulkUploadedOpeningBalance: Boolean(bulkEntity),
      totalCommission,
      totalCharged: paymentSummary.totalCharged,
      totalPayments: paymentSummary.payments,
      totalAdvances: paymentSummary.advances,
      closingBalance: paymentSummary.balanceToPay,
      isUnknown: entity.entityType === "unknown",
    };
  });
  const matchedIoRecipientIds = [
    ...new Set([
      ...Object.values(uniqueIds).flat(),
      ...Object.values(ioRecipientMappings)
        .map((mapping) => String(mapping.recipientCrmId || "").trim())
        .filter(Boolean),
    ]),
  ];
  return {
    rows: [...adviserRows, ...nonAdviserRows],
    matchedIoRecipientIds,
  };
};

export const downloadMonthlyPaymentsWorkbook = async ({
  selectedMonth,
  chargeRecords = [],
  balanceChargeRecords = chargeRecords,
  userEmail = "",
  incomePeriods: suppliedIncomePeriods,
  advisers = [],
  balanceAdvisers = advisers,
  closingBalanceRowsOverride = null,
}) => {
  const incomePeriodsPromise = suppliedIncomePeriods
    ? Promise.resolve(suppliedIncomePeriods)
    : getIncomePeriods();
  const quilterPromise = getQuilterStatement(selectedMonth);
  const finplanPromise = getFinplanStatement(selectedMonth);
  const suspensePromise = getSuspenseStatement(selectedMonth);
  const enrichedChargeRecordsPromise = Promise.all(
    chargeRecords.map(async (record) => {
      if (record.chargeType !== "AML Checks" || !record.__ref) return record;
      try {
        return {
          ...record,
          client: (await loadAMLClientName(record)) || record.client || "",
        };
      } catch {
        return record;
      }
    }),
  );
  const manualPaymentsPromise = getMonthlyManualPayments();
  const ioRecipientMappingsPromise = getIORecipientMappings();
  const currentIoTotalsPromise = incomePeriodsPromise.then((incomePeriods) => {
    const currentMonth = monthKeyFromDate(
      incomePeriods[0]?.endDatetime ||
        incomePeriods[0]?.endDate ||
        incomePeriods[0]?.incomePeriodEndDatetime,
    );
    return selectedMonth === currentMonth
      ? getCurrentFCITotalsByAdviser(userEmail)
      : null;
  });
  const ioPromise = incomePeriodsPromise.then((incomePeriods) => {
    const currentMonth = monthKeyFromDate(
      incomePeriods[0]?.endDatetime ||
        incomePeriods[0]?.endDate ||
        incomePeriods[0]?.incomePeriodEndDatetime,
    );
    const matchingPeriod = incomePeriods.find(
      (period) => getIncomePeriodStartMonthKey(period) === selectedMonth,
    );
    const period = selectedMonth === currentMonth
      ? "current"
      : String(matchingPeriod?.incomePeriodId || "");

    return period
      ? getFCIStatementsPage(1, 100000, userEmail, {
          _period: period,
          _sort: "PaymentCreated",
          _direction: "desc",
          _export: "true",
        })
      : { rows: [] };
  });

  const [
    ioResult,
    quilter,
    finplan,
    suspense,
    enrichedChargeRecords,
    manualPayments,
    ioRecipientMappings,
    currentIoTotals,
  ] = await Promise.all([
    ioPromise,
    quilterPromise,
    finplanPromise,
    suspensePromise,
    enrichedChargeRecordsPromise,
    manualPaymentsPromise,
    ioRecipientMappingsPromise,
    currentIoTotalsPromise,
  ]);

  const workbook = XLSX.utils.book_new();
  const charges = chargeRows(enrichedChargeRecords);
  appendChargeSheets(workbook, charges, advisers);

  const io = ioRows(ioResult.rows || []);
  const ioHeaders = ["Recipient", "Payment Created", "Selling Adviser", "Client Name", "Policy Number", "Policy Provider", "Business Type", "Commission Type", "Payment Amount", "Gross Received", "Split", "Introducers"];
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(ioHeaders, io, { includeHyperlinks: false }),
    "IO Summary",
  );

  const quilterRows = uploadedRows(
    quilter,
    normaliseQuilterRows,
    QUILTER_TABLE_HEADERS,
    advisers,
  );
  const quilterHeaders = QUILTER_TABLE_HEADERS
    .filter((header) => header !== "Index")
    .map(heading);
  XLSX.utils.book_append_sheet(workbook, sheetFromRows(quilterHeaders, quilterRows), "Quilter Summary");

  const normalisedFinplanRows = finplanCommissionRows({
    statement: finplan,
    advisers,
    statementMonth: selectedMonth,
  });
  const uniqueCommissionRows = dedupeCommissionRows([
      ...(ioResult.rows || []).map((row) => ({
        ...row,
        __source: "io",
        __statementMonth: selectedMonth,
      })),
      ...normaliseQuilterRows(quilter?.rows || []).map((row) => ({
        ...row,
        __source: "quilter",
        __statementMonth: selectedMonth,
      })),
      ...normalisedFinplanRows.map((row) => ({
        ...row,
        __statementMonth: selectedMonth,
      })),
    ]);
  const includedFinplanKeys = new Set(
    uniqueCommissionRows
      .filter((row) => row.__source === "finplan")
      .map(commissionRowKey),
  );
  const emittedFinplanKeys = new Set();
  const finplanDetailRows = normalisedFinplanRows
    .filter((row) => {
      const rowKey = commissionRowKey({
        ...row,
        __statementMonth: selectedMonth,
      });
      if (!includedFinplanKeys.has(rowKey) || emittedFinplanKeys.has(rowKey)) {
        return false;
      }
      emittedFinplanKeys.add(rowKey);
      return true;
    })
    .map((row) => ({
    Recipient: row.Recipient,
    "Recipient Email": row.RecipientEmail,
    "Payment Created": row.PaymentCreated,
    "Selling Adviser": row.PolicyAdviser,
    "Selling Adviser Email": row.SellingAdviserEmail,
    "Client Name": row.ClientName,
    "Policy Number": row.PolicyAccountNumber,
    "Policy Provider": row.PolicyProvider,
    "Business Type": row.BusinessType,
    "Commission Type": row.CommissionType,
    "Payment Amount": numberValue(row.PaymentAmount),
    "Gross Received": numberValue(row.GrossReceived),
    Split: numberValue(row.Split) === ""
      ? ""
      : Number(numberValue(row.Split)) / 100,
    Introducers: introducerText(row.Introducers),
    Source: row.Source,
    }));
  const finplanRows = finplanDetailRows;
  const finplanHeaders = [
    ...FINPLAN_TEMPLATE_HEADERS,
    "Recipient Email",
    "Selling Adviser Email",
    "Source",
  ];
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(finplanHeaders, finplanRows),
    "Finplan",
  );

  const suspenseRows = consolidateGlobalSummaryRows({
    rows: uploadedRows(
      suspense,
      normaliseSuspenseRows,
      SUSPENSE_TABLE_HEADERS,
      advisers,
    ),
    advisers,
    emailKeys: ["__email"],
    entryKeys: ["__entryKey"],
    nameKey: "Mapped Adviser",
    nameWithEmail: true,
    sumColumns: ["Suspense Amount", "Suspense Charge"],
    dateColumns: ["Date"],
  });
  const suspenseHeaders = SUSPENSE_TABLE_HEADERS
    .filter((header) => header !== "Index")
    .map(heading);
  XLSX.utils.book_append_sheet(workbook, sheetFromRows(suspenseHeaders, suspenseRows), "Suspense");

  const payments = paymentRows(manualPayments, selectedMonth);
  const {
    rows: balanceRows,
    matchedIoRecipientIds,
  } = await buildBalanceRows({
    selectedMonth,
    chargeRecords: enrichedChargeRecords,
    balanceChargeRecords,
    manualPayments,
    ioRows: ioResult.rows || [],
    ioTotals: currentIoTotals,
    quilterStatement: quilter,
    finplanStatement: finplan,
    suspenseStatement: suspense,
    advisers: balanceAdvisers,
    ioRecipientMappings,
  });
  const unknownUsersCommissionRows = buildUnknownUsersCommissionRows({
    selectedMonth,
    ioRows: ioResult.rows || [],
    quilterStatement: quilter,
    manualPayments,
    advisers,
    ioRecipientMappings,
    matchedIoRecipientIds,
  });
  const unknownUsersCommissionHeaders = [
    "Category",
    "Name",
    "Identifier",
    "Payment Count",
    "Opening Balance",
    "Total Commission",
    "Total Charged",
    "Payments",
    "Advances",
    "Closing Balance",
  ];
  const unknownUsersCommissionTotal = unknownUsersCommissionRows.reduce(
    (total, row) => ({
      Category: "Total",
      Name: "",
      Identifier: "",
      "Payment Count": total["Payment Count"] + row["Payment Count"],
      "Opening Balance": total["Opening Balance"] + row["Opening Balance"],
      "Total Commission": total["Total Commission"] + row["Total Commission"],
      "Total Charged": total["Total Charged"] + row["Total Charged"],
      Payments: total.Payments + row.Payments,
      Advances: total.Advances + row.Advances,
      "Closing Balance": total["Closing Balance"] + row["Closing Balance"],
    }),
    {
      Category: "Total",
      Name: "",
      Identifier: "",
      "Payment Count": 0,
      "Opening Balance": 0,
      "Total Commission": 0,
      "Total Charged": 0,
      Payments: 0,
      Advances: 0,
      "Closing Balance": 0,
    },
  );
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(unknownUsersCommissionHeaders, [
      ...unknownUsersCommissionRows,
      unknownUsersCommissionTotal,
    ]),
    "Unknown Users Commission",
  );
  const paymentHeaders = ["Adviser", "Adviser Email", "Date", "Description", "Notes", "Amount"];
  const additionalPaymentRows = globalChargeSheetRows({
    category: "Additional Payments",
    rows: payments.filter((row) => row["Payment Type"] === "Additional Payment"),
    advisers,
  });
  const advanceRows = globalChargeSheetRows({
    category: "Advances",
    rows: payments.filter((row) => row["Payment Type"] === "Advance"),
    advisers,
  });
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(paymentHeaders, additionalPaymentRows),
    "Additional Payments",
  );
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(paymentHeaders, advanceRows),
    "Advances",
  );
  const openingBalanceRows = consolidateGlobalSummaryRows({
    rows: balanceRows
    .filter(
      (row) =>
        row.hasOpeningBalance && isNonZero(row.openingBalance),
    )
    .sort(
      (left, right) =>
        left.openingBalance - right.openingBalance,
    )
    .map((row) => ({
      Adviser: row.adviser,
      "Adviser Email": row.email,
      Practice: row.practice,
      "Opening Balance": row.openingBalance,
    })),
    advisers,
    emailKeys: ["Adviser Email"],
    entryKeys: ["Adviser"],
    sumColumns: ["Opening Balance"],
  });
  const openingBalanceHeaders = [
    "Adviser",
    "Adviser Email",
    "Practice",
    "Opening Balance",
  ];
  XLSX.utils.book_append_sheet(
    workbook,
    balanceSheetFromRows(
      openingBalanceHeaders,
      openingBalanceRows,
      "Opening Balance",
    ),
    "Opening Balances",
  );

  const closingBalanceSourceRows = Array.isArray(closingBalanceRowsOverride)
    ? closingBalanceRowsOverride
    : includedClosingBalanceRows(balanceRows);
  const closingBalanceRows = [...closingBalanceSourceRows]
    .sort((left, right) => left.closingBalance - right.closingBalance)
    .map((row) => ({
      Adviser: row.adviser,
      "Adviser Email": row.email,
      Practice: row.practice,
      "Closing Balance": row.closingBalance,
    }));
  const closingBalanceHeaders = [
    "Adviser",
    "Adviser Email",
    "Practice",
    "Closing Balance",
  ];
  XLSX.utils.book_append_sheet(
    workbook,
    balanceSheetFromRows(
      closingBalanceHeaders,
      closingBalanceRows,
      "Closing Balance",
    ),
    "Closing Balances",
  );

  const commissionRows = consolidateGlobalSummaryRows({
    rows: balanceRows
    .filter(
      (row) =>
        isNonZero(row.openingBalance) ||
        isNonZero(row.totalCommission) ||
        isNonZero(row.totalCharged) ||
        isNonZero(row.totalPayments) ||
        isNonZero(row.totalAdvances) ||
        isNonZero(row.closingBalance),
    )
    .map((row) => ({
      Adviser: row.adviser,
      "Linked Adviser Email": row.email,
      "Opening Balance": row.openingBalance,
      "Total Commission": row.totalCommission,
      "Total Charged": row.totalCharged,
      Payments: row.totalPayments,
      Advances: row.totalAdvances,
      "Closing Balance": row.closingBalance,
    })),
    advisers,
    emailKeys: ["Linked Adviser Email"],
    entryKeys: ["Adviser"],
    sumColumns: [
      "Opening Balance",
      "Total Commission",
      "Total Charged",
      "Payments",
      "Advances",
      "Closing Balance",
    ],
  }).sort((left, right) => left.Adviser.localeCompare(right.Adviser, "en-GB"));
  const commissionHeaders = ["Adviser", "Linked Adviser Email", "Opening Balance", "Total Commission", "Total Charged", "Payments", "Advances", "Closing Balance"];
  XLSX.utils.book_append_sheet(
    workbook,
    sheetFromRows(commissionHeaders, commissionRows),
    "Commission Statements",
  );

  const buffer = XLSX.write(workbook, { bookType: "xlsx", type: "array", cellStyles: true });
  saveAs(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `monthly-payments-${selectedMonth}.xlsx`,
  );
};
